import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../contexts/AuthContext'
import * as generationService from '../services/generationService'
import { base64ToFile } from '../services/geminiService'
import { extractErrorMessage } from '../utils/errorHandler'
import GenerationLoader from '../components/GenerationLoader'
import GenerationHistory from '../components/GenerationHistory'
import LengthSelector from '../components/LengthSelector'
import {
  FiCamera,
  FiLoader,
  FiAlertTriangle,
  FiDownload,
  FiArrowLeft,
  FiSettings,
  FiImage,
  FiLayers,
  FiEdit3,
  FiZap,
  FiCheck,
  FiX,
  FiInfo,
  FiClock,
} from 'react-icons/fi'
import './GeneratorPage.css'

const MAX_PROMPT_LENGTH_MAGIC = 500
const MAX_PROMPT_LENGTH_GEN = 2000

const ImageGeneratorPage = () => {
  const { token } = useAuthContext()
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState('')
  const [generatedImages, setGeneratedImages] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [lastPrompt, setLastPrompt] = useState('')
  const [generationTime, setGenerationTime] = useState(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const startTimeRef = useRef(null)
  const timerIntervalRef = useRef(null)

  // F.R.A.N.C.A. AI generation features
  const [availableModels, setAvailableModels] = useState([])
  const [selectedModel, setSelectedModel] = useState('advanced')
  const [generationMode, setGenerationMode] = useState('text')
  const [inputImages, setInputImages] = useState([])
  const [inputImagePreviews, setInputImagePreviews] = useState([])
  const [showSettings, setShowSettings] = useState(false)

  // Magic prompt states
  const [isMagicLoading, setIsMagicLoading] = useState(false)
  const [magicPromptLength, setMagicPromptLength] = useState('medium')
  const [useMagicEdit, setUseMagicEdit] = useState(false)
  const [originalPrompt, setOriginalPrompt] = useState('')

  // History states
  const [imageHistory, setImageHistory] = useState([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)

  const isPromptTooLong = useMemo(() => prompt.length > MAX_PROMPT_LENGTH_GEN, [prompt])
  const isMagicInputTooLong = useMemo(
    () => prompt.length > MAX_PROMPT_LENGTH_MAGIC,
    [prompt]
  )

  // Fetch image history
  const fetchHistory = React.useCallback(async () => {
    if (!token) return
    setIsLoadingHistory(true)
    try {
      const history = await generationService.getImageHistory(token, 5, 60)
      setImageHistory(history)
    } catch (error) {
      console.error('Failed to load image history:', error)
    } finally {
      setIsLoadingHistory(false)
    }
  }, [token])

  // Load available models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const models = await generationService.getAvailableModels(token)
        setAvailableModels(models.models || [])
      } catch (error) {
        console.error('Failed to load models:', error)
      }
    }

    if (token) {
      loadModels()
      fetchHistory() // Load history on mount
    }
  }, [token, fetchHistory])

  const handleBack = () => {
    navigate(-1)
  }

  const getErrorMessage = err => {
    const apiResponseData = err.response?.data
    const apiErrorDetail = apiResponseData?.detail

    if (
      Array.isArray(apiErrorDetail) &&
      apiErrorDetail.length > 0 &&
      apiErrorDetail[0].msg
    ) {
      const msg = apiErrorDetail[0].msg
      const location = apiErrorDetail[0].loc.slice(-1)
      return `Errore di validazione sul campo '${location}': ${msg}. Lunghezza massima consentita: ${MAX_PROMPT_LENGTH_GEN} caratteri.`
    }

    if (typeof apiResponseData === 'string') {
      return apiResponseData
    }

    if (typeof apiErrorDetail === 'string' || typeof apiErrorDetail === 'object') {
      return apiErrorDetail
    }

    return err.message || 'Si è verificato un errore durante la generazione. Riprova.'
  }

  const handleEditGeneratedImage = imageData => {
    setGenerationMode('edit')
    const imageFile = base64ToFile(imageData, `generated_edit_${Date.now()}.png`)
    setInputImages([imageFile])
    setGeneratedImages([])
    setPrompt('')
    setError('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleMagicPrompt = async () => {
    if (!prompt.trim() || isLoading || isMagicLoading || isMagicInputTooLong) {
      setError(
        `Il prompt per Magic Prompt deve contenere al massimo ${MAX_PROMPT_LENGTH_MAGIC} caratteri.`
      )
      return
    }
    setIsMagicLoading(true)
    setError('')
    try {
      const enhancedPrompt = await generationService.getMagicPrompt(
        token,
        prompt,
        magicPromptLength
      )
      setPrompt(enhancedPrompt)
      if (generationMode === 'edit' && inputImages.length > 0) {
        setUseMagicEdit(true)
        setOriginalPrompt(prompt)
      }
    } catch (err) {
      const errorMessage = extractErrorMessage(
        err,
        'Si prega di limitare i prompt a 500 caratteri o meno per ottenere i migliori risultati nel generatore di prompt magici.'
      )
      setError(errorMessage)
    } finally {
      setIsMagicLoading(false)
    }
  }

  const handleGenerate = async e => {
    e.preventDefault()
    if (!prompt.trim() || isLoading || isPromptTooLong) {
      if (isPromptTooLong) {
        setError(
          `Il prompt è troppo lungo. Lunghezza massima: ${MAX_PROMPT_LENGTH_GEN} caratteri.`
        )
      }
      return
    }

    if (generationMode === 'edit' && inputImages.length === 0) {
      setError("Per favore carica un'immagine da modificare.")
      return
    }
    if (generationMode === 'multi' && inputImages.length < 2) {
      setError('Per favore carica almeno 2 immagini per la generazione multi-immagine.')
      return
    }

    setIsLoading(true)
    setError('')
    setGeneratedImages([])
    setLastPrompt(prompt)
    setGenerationTime(null)
    setElapsedSeconds(0)
    startTimeRef.current = Date.now()

    // Start timer - update every second
    timerIntervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
        setElapsedSeconds(elapsed)
      }
    }, 1000)

    try {
      let response

      if (useMagicEdit && generationMode === 'edit' && inputImages.length > 0) {
        response = await generationService.magicEditImage(
          token,
          originalPrompt,
          inputImages[0],
          selectedModel,
          magicPromptLength
        )
        setUseMagicEdit(false)
        setOriginalPrompt('')
      } else {
        switch (generationMode) {
          case 'text':
            response = await generationService.generateImage(
              token,
              prompt,
              selectedModel
            )
            break
          case 'edit':
            response = await generationService.editImage(
              token,
              prompt,
              inputImages[0],
              selectedModel
            )
            break
          case 'multi':
            response = await generationService.generateFromMultipleImages(
              token,
              prompt,
              inputImages,
              selectedModel
            )
            break
          default:
            throw new Error('Invalid generation mode')
        }
      }

      setGeneratedImages(response.images || [])

      // Calculate generation time
      if (startTimeRef.current) {
        const timeTaken = ((Date.now() - startTimeRef.current) / 1000).toFixed(1)
        setGenerationTime(parseFloat(timeTaken))
      }

      // Refresh history after successful generation
      fetchHistory()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
      startTimeRef.current = null
    }
  }

  useEffect(() => {
    if (inputImages.length === 0) {
      setInputImagePreviews([])
      return
    }

    const previews = inputImages.map(file => {
      if (!file.type.startsWith('image/')) {
        return { url: null }
      }
      return { url: URL.createObjectURL(file) }
    })
    setInputImagePreviews(previews)

    return () => {
      previews.forEach(preview => {
        if (preview.url) {
          URL.revokeObjectURL(preview.url)
        }
      })
    }
  }, [inputImages])

  const handleImageUpload = e => {
    const files = Array.from(e.target.files)
    const maxFiles = generationMode === 'edit' ? 1 : 3

    if (files.length > maxFiles) {
      setError(
        `Massimo ${maxFiles} immagine${
          maxFiles > 1 ? 's' : ''
        } consentita per la modalità ${generationMode}.`
      )
      return
    }

    const validFiles = files.filter(file => {
      const validTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
      ]
      const maxSize = 10 * 1024 * 1024
      return validTypes.includes(file.type) && file.size <= maxSize
    })

    if (validFiles.length !== files.length) {
      setError(
        'Alcuni file non sono validi. Si prega di utilizzare immagini JPEG, PNG, GIF o WebP sotto i 10MB.'
      )
    } else {
      setError('')
    }

    setInputImages(validFiles)
  }

  const removeImage = index => {
    setInputImages(prev => prev.filter((_, i) => i !== index))
  }

  const getModeLabel = () => {
    switch (generationMode) {
      case 'text':
        return 'Text to Image'
      case 'edit':
        return 'Image Editing'
      case 'multi':
        return 'Multi-Image Generation'
      default:
        return 'Image Generation'
    }
  }

  const getPromptPlaceholder = () => {
    switch (generationMode) {
      case 'text':
        return 'Es: Crea un data center a 4 piani con tetto a pannelli solari, architettura moderna, vista frontale professionale...'
      case 'edit':
        return "Descrivi come modificare l'immagine caricata, es: Aggiungi un sistema di raffreddamento esterno al data center..."
      case 'multi':
        return "Descrivi come combinare le immagini caricate, es: Combina l'architettura del data center con l'infrastruttura di rete..."
      default:
        return "Descrivi l'immagine che vuoi creare..."
    }
  }

  const getModelDisplayName = model => {
    const modelNames = {
      fast: 'Generazione Veloce',
      advanced: 'Generazione Avanzata',
    }
    return modelNames[model] || model
  }

  return (
    <div className="generator-page-area">
      <div className="generator-header">
        <div className="header-controls">
          <button
            onClick={handleBack}
            className="back-button"
            aria-label="Torna indietro"
          >
            <FiArrowLeft />
          </button>
          <h1>
            <FiCamera /> Generatore di Immagini AI
          </h1>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`settings-button ${showSettings ? 'active' : ''}`}
            aria-label="Impostazioni"
          >
            <FiSettings />
          </button>
        </div>

        {/* Mode Selection */}
        <div className="generation-mode-selector">
          <button
            className={`mode-button ${generationMode === 'text' ? 'active' : ''}`}
            onClick={() => {
              setGenerationMode('text')
              setInputImages([])
            }}
          >
            <FiCamera />
            <span>Text to Image</span>
          </button>
          <button
            className={`mode-button ${generationMode === 'edit' ? 'active' : ''}`}
            onClick={() => {
              setGenerationMode('edit')
              setInputImages([])
            }}
          >
            <FiImage />
            <span>Image Editing</span>
          </button>
          <button
            className={`mode-button ${generationMode === 'multi' ? 'active' : ''}`}
            onClick={() => {
              setGenerationMode('multi')
              setInputImages([])
            }}
          >
            <FiLayers />
            <span>Multi-Image</span>
          </button>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="settings-panel">
            <div className="settings-header">
              <h3>Impostazioni Generazione</h3>
              <button onClick={() => setShowSettings(false)} className="close-settings">
                <FiX />
              </button>
            </div>
            <div className="setting-group">
              <label htmlFor="model-select">
                Modello di Generazione
                <span className="model-info">
                  <FiInfo />
                  <div className="model-tooltip">
                    {selectedModel === 'fast'
                      ? 'Più veloce, ideale per prototipi e idee rapide'
                      : 'Qualità superiore, perfetto per risultati finali'}
                  </div>
                </span>
              </label>
              <select
                id="model-select"
                value={selectedModel}
                onChange={e => setSelectedModel(e.target.value)}
                className="model-select"
              >
                {availableModels.map(model => (
                  <option key={model} value={model}>
                    {getModelDisplayName(model)}
                  </option>
                ))}
              </select>
              <div className="model-description">
                {selectedModel === 'fast'
                  ? 'Generazione rapida con risultati di buona qualità'
                  : 'Generazione avanzata con massima qualità e dettagli'}
              </div>
            </div>
          </div>
        )}

        <div className="mode-description">
          <strong>{getModeLabel()}:</strong>
          {generationMode === 'text'
            ? " Descrivi l'immagine che vuoi creare. Sii dettagliato per risultati migliori."
            : generationMode === 'edit'
            ? " Carica un'immagine e descrivi come modificarla."
            : ' Carica 2-3 immagini e descrivi come combinarle.'}
        </div>
      </div>

      <div className="generator-content">
        <div className="generator-form-container">
          <form onSubmit={handleGenerate} className="generator-form">
            {/* Image Upload Section */}
            {(generationMode === 'edit' || generationMode === 'multi') && (
              <div className="image-upload-section">
                <label className="upload-label">
                  <input
                    type="file"
                    accept="image/*"
                    multiple={generationMode === 'multi'}
                    onChange={handleImageUpload}
                    className="upload-input"
                    disabled={isLoading}
                  />
                  <div className="upload-area">
                    <FiImage size={24} />
                    <span>
                      {generationMode === 'edit'
                        ? 'Carica immagine da modificare'
                        : 'Carica 2-3 immagini da combinare'}
                    </span>
                    <small>JPEG, PNG, GIF, WebP - Max 10MB</small>
                  </div>
                </label>

                {/* Preview uploaded images */}
                {inputImages.length > 0 && (
                  <div className="uploaded-images-preview">
                    <div className="preview-header">
                      <span>Immagini Caricate ({inputImages.length})</span>
                    </div>
                    <div className="preview-grid">
                      {inputImages.map((file, index) => (
                        <div key={index} className="uploaded-image-item">
                          <div className="image-preview">
                            <img
                              src={inputImagePreviews[index]?.url || ''}
                              alt={`Upload ${index + 1}`}
                              className="uploaded-image-thumb"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="remove-image-button"
                              disabled={isLoading}
                            >
                              <FiX />
                            </button>
                          </div>
                          <span className="image-name">{file.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Prompt Input Section */}
            <div className="prompt-input-section">
              <div>
                <label className="prompt-label">
                  Prompt di Generazione
                  {useMagicEdit && (
                    <span className="magic-badge">
                      <FiZap /> Magic Edit
                    </span>
                  )}
                </label>

                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder={getPromptPlaceholder()}
                  className={`generator-textarea ${
                    isPromptTooLong ? 'error-border' : ''
                  } ${useMagicEdit ? 'magic-edit-active' : ''}`}
                  rows="4"
                  disabled={isLoading || isMagicLoading}
                />
              </div>
              <div className="prompt-controls-row">
                <div className="char-counter-container">
                  <span
                    className={`char-counter ${isPromptTooLong ? 'limit-reached' : ''}`}
                  >
                    {prompt.length} / {MAX_PROMPT_LENGTH_GEN}
                  </span>
                  {isPromptTooLong && (
                    <span className="char-warning">Prompt troppo lungo</span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <LengthSelector
                    selectedLength={magicPromptLength}
                    onLengthChange={setMagicPromptLength}
                    disabled={isLoading || isMagicLoading}
                  />
                  <button
                    type="button"
                    className="magic-prompt-button"
                    onClick={handleMagicPrompt}
                    disabled={
                      !prompt.trim() ||
                      isLoading ||
                      isMagicLoading ||
                      isMagicInputTooLong
                    }
                    title="Migliora il prompt con l'IA"
                  >
                    {isMagicLoading ? <FiLoader className="spinner-icon" /> : <FiZap />}
                    <span>Magic Prompt</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Magic Edit Indicator */}
            {useMagicEdit && (
              <div className="magic-edit-indicator">
                <FiZap className="indicator-icon" />
                <span>
                  Magic Edit attivo - Prompt migliorato mostrato sopra. Puoi modificarlo
                  prima di generare.
                </span>
              </div>
            )}

            <button
              type="submit"
              className="generate-button"
              disabled={
                isLoading ||
                !prompt.trim() ||
                isPromptTooLong ||
                (generationMode === 'edit' && inputImages.length === 0) ||
                (generationMode === 'multi' && inputImages.length < 2)
              }
            >
              {isLoading ? (
                <>
                  <FiLoader className="spinner-icon" />
                  <span>Generazione in corso...</span>
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </>
              ) : (
                <>
                  <FiCamera />
                  <span>Genera Immagine</span>
                </>
              )}
            </button>
          </form>
        </div>

        <div className="generator-output-container">
          {error && (
            <div className="error-placeholder">
              <FiAlertTriangle className="error-icon" />
              <div className="error-content">
                <h3>Errore di Generazione</h3>
                <p>{error}</p>
              </div>
            </div>
          )}

          {!error && isLoading && (
            <GenerationLoader
              type="image"
              elapsedSeconds={elapsedSeconds}
              message="Stiamo generando la tua immagine con l'IA. Potrebbe richiedere alcuni secondi..."
            />
          )}

          {!isLoading && !error && generatedImages.length > 0 && (
            <div className="images-result-container">
              <div className="results-header">
                <h3>Immagini Generate</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {generationTime && (
                    <span className="generation-time-badge">
                      <FiClock /> Generato in {generationTime}s
                    </span>
                  )}
                  <span className="results-count">
                    {generatedImages.length} risultato
                    {generatedImages.length !== 1 ? 'i' : ''}
                  </span>
                </div>
              </div>
              <div className="images-grid">
                {generatedImages.map((image, index) => (
                  <div key={index} className="image-result-card">
                    <div className="card-header">
                      <span className="image-number">Immagine {index + 1}</span>
                      {image.safety_ratings && (
                        <div className="safety-badge">
                          <FiCheck className="safety-icon" />
                          <span>Sicura</span>
                        </div>
                      )}
                    </div>
                    <div className="image-container">
                      <img
                        src={`data:image/png;base64,${image.data}`}
                        alt={`${lastPrompt} - Result ${index + 1}`}
                        className="generated-image"
                      />
                      <div className="image-overlay">
                        <div className="action-buttons">
                          <a
                            href={`data:image/png;base64,${image.data}`}
                            download={`${lastPrompt
                              .substring(0, 20)
                              .replace(/\s/g, '_')}_${index + 1}.png`}
                            className="download-action-btn"
                          >
                            <FiDownload />
                            <span>Scarica</span>
                          </a>
                          <button
                            onClick={() => handleEditGeneratedImage(image.data)}
                            className="edit-action-btn"
                          >
                            <FiEdit3 />
                            <span>Modifica</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer">
                      <span className="prompt-preview">
                        {lastPrompt.substring(0, 80)}
                        {lastPrompt.length > 80 ? '...' : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isLoading && !error && generatedImages.length === 0 && (
            <div className="idle-placeholder">
              <div className="idle-content">
                <FiCamera className="idle-icon" />
                <h3>Pronto per Creare</h3>
                <p>
                  {generationMode === 'text'
                    ? "Inserisci un prompt descrittivo per generare immagini uniche con l'IA."
                    : generationMode === 'edit'
                    ? "Carica un'immagine e descrivi le modifiche che desideri applicare."
                    : 'Carica multiple immagini e descrivi come combinarle in una nuova creazione.'}
                </p>
              </div>
            </div>
          )}

          {/* Recent History */}
          <GenerationHistory
            items={imageHistory}
            type="image"
            isLoading={isLoadingHistory}
            token={token}
            onItemClick={item => {
              // User can click to reload a previous prompt
              setPrompt(item.prompt || '')
              if (item.model) setSelectedModel(item.model)
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default ImageGeneratorPage
